import React, { FC, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import isUndefined from 'lodash/isUndefined';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import Currency from 'components/currency';
import { MenuObjectTypes } from 'enums/menu';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useOutsideClick from 'hooks/use-on-new-click-outside/use-on-new-click-outside';
import { usePrevious } from 'hooks/use-previous';
import Offer from 'pages/cart/your-cart/offer';
import { selectors, useAppSelector } from 'state/global-state';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewardsFeedback } from 'state/loyalty/hooks/use-loyalty-rewards-feedback';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useReversedUIContext } from 'state/reversed-ui';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { isNative } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import CartPreviewEntry from './cart-preview-entry';
import defaultConfig from './config';
import { CHECKOUT_BUTTON_PADDING } from './constants';
import LimitMessage from './limit-message';
import { MinOrderMessage } from './min-order-message';
import MinimumMessage from './minimum-message';
import { ButtonContainer } from './styled/button-container';
import { ActionButtonStyled } from './styled/cart-button';
import { CartPreviewContainer } from './styled/cart-preview-container';
import { EmptyCartMessage } from './styled/empty-cart-message';
import { MessageContainer } from './styled/message-container';
import { TotalsContainer } from './styled/totals-container';
import theme from './theme';
import { ICartPreviewProps } from './types';

const CartHoverContainer = styled.div`
  box-shadow: ${Styles.boxShadow};
  background: ${Styles.color.white};
  position: absolute;
  right: 0;
  top: 4.5rem;
  transition: all 200ms ease-out;

  ${Styles.desktop`
    border-radius: 10px;
    max-height: calc(100vh - 10rem);
    overflow: hidden;
    z-index: ${Styles.zIndex.normal}
  `}
`;

const CartScrollContainer = styled.div`
  ${Styles.desktop`
    max-height: calc(100vh - 10rem - 203px);
    overflow-y: auto;
    position: relative;
  `}
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${Styles.color.black};
  margin-bottom: 0.5rem;

  p {
    font-size: 1rem;
    margin: 0;
  }
`;

export const CartPreviewItem = styled.div`
  border: 1px solid ${Styles.color.grey.four};
  border-radius: ${Styles.borderRadius};
  display: grid;
  grid-template-columns: 2fr 6fr 1fr;
  padding: 0.5rem;
  width: 100%;
  min-height: 102px;

  > div {
    padding: 0 1rem;

    p {
      color: ${theme.cartPreviewItemPColor};
      font-size: 0.75rem;
      font-weight: 300;
      line-height: 16px;
      margin: 0;
    }

    > p:last-of-type {
      margin-top: 14px;
    }
  }
`;

const ButtonContainerShadow = styled.div<{ $boxShadow: boolean }>`
  bottom: 0;
  box-shadow: 0px -4px 4px 0 rgba(0, 0, 0, 0.1);
  left: 0;
  opacity: ${props => (props.$boxShadow ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  top: 0;
  transition: 0.2s opacity linear;
  right: 0;
`;

const CartPreview: FC<ICartPreviewProps> = ({
  id,
  calculateCartTotal,
  checkoutPriceLimit,
  discountOffers,
  isStoreOpenAndAvailable,
  openOfferCheckoutConfirmationModal,
  canUserCheckout,
  isCartTotalGreaterThanDiscountOfferValue,
  isCartEmpty,
  cartPreviewEntries,
  cartEntries,
  navigate,
  loyaltyEnabled,
  appliedLoyaltyRewards,
  hideCartPreview,
  cartButton,
}) => {
  const [boxShadow, setBoxShadow] = useState(false);
  const scrollEl = useRef<HTMLDivElement | null>(null);
  const cartPreviewRef = useRef<HTMLDivElement | null>(null);

  const shouldShowBoxShadow = useCallback(() => {
    if (scrollEl.current) {
      const scrollElTop = scrollEl.current.scrollTop;
      const scrollElHeight = scrollEl.current.offsetHeight;
      const firstChild = scrollEl.current.childNodes[0] as HTMLElement;
      const childHeight = firstChild ? firstChild.offsetHeight : 0;
      return childHeight && childHeight > scrollElTop + scrollElHeight;
    }
    return false;
  }, []);

  useOutsideClick(cartPreviewRef, hideCartPreview, cartButton);

  //TODO ICFE-518 - replace js for showing / hiding box shadow with plain css
  const handleScroll = useCallback(() => {
    const showShadow = !!shouldShowBoxShadow();
    setBoxShadow(showShadow);
  }, [shouldShowBoxShadow]);

  useEffect(() => {
    if (scrollEl.current && cartEntries.length) {
      handleScroll();
    }
  }, [cartEntries, handleScroll]);

  const handleCheckout = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();

    if (!isStoreOpenAndAvailable) {
      navigate(routes.storeLocator);
      return;
    }

    // Open the checkout confirmation modal in a native environment
    // and when the cart total is greater than a certain value
    if (isNative && isCartTotalGreaterThanDiscountOfferValue()) {
      openOfferCheckoutConfirmationModal();
      return;
    }

    navigate(routes.cart);
  };

  const handleOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    const childElementIsFocused = event.currentTarget.contains(event.relatedTarget);
    if (!childElementIsFocused) {
      hideCartPreview();
    }
  };

  const total = calculateCartTotal();

  return createPortal(
    <CartHoverContainer
      id={id}
      data-testid="cart-preview"
      onBlur={handleOnBlur}
      ref={cartPreviewRef}
    >
      <CartScrollContainer ref={scrollEl} onScroll={handleScroll}>
        {(cartPreviewEntries.length > 0 || !!discountOffers?.length) && (
          <CartPreviewContainer>
            {discountOffers?.map(discountOffer => (
              <Offer
                bgColor="#f4f5f5"
                fill={Styles.color.grey.five}
                key={discountOffer._id}
                borderColor="#b6b6b6"
                selectedOffer={discountOffer}
                isValid
              />
            ))}
            {cartPreviewEntries.map(item => {
              return (
                <CartPreviewEntry
                  key={item.cartId}
                  item={item}
                  rewardApplied={
                    loyaltyEnabled && appliedLoyaltyRewards[item.cartId]?.timesApplied > 0
                  }
                />
              );
            })}
          </CartPreviewContainer>
        )}
      </CartScrollContainer>
      <ButtonContainer>
        <ButtonContainerShadow $boxShadow={boxShadow} />
        <TotalsContainer data-testid="cart-preview-total">
          {isCartEmpty && (
            <EmptyCartMessage>
              <FormattedMessage
                id="emptyCartMessage"
                defaultMessage="You don't have anything in your cart yet!"
                description="Message indicating cart is empty"
              />
            </EmptyCartMessage>
          )}
          <Row>
            <p>
              <b>Total*</b>
            </p>
            <p>
              <b>
                <Currency amount={total} />
              </b>
            </p>
          </Row>
        </TotalsContainer>
        <MessageContainer>
          <div data-testid="min-order-message">
            {isCartTotalGreaterThanDiscountOfferValue() && <MinOrderMessage />}
          </div>
          <LimitMessage limit={checkoutPriceLimit} totalAmount={total} />
          <MinimumMessage />
        </MessageContainer>
        <Box width="100%" padding={defaultConfig.isSquare ? '0' : CHECKOUT_BUTTON_PADDING}>
          <ActionButton
            fullWidth
            data-testid="checkout-action-button"
            onClick={handleCheckout}
            disabled={!canUserCheckout}
            isSquare={defaultConfig.isSquare}
          >
            <FormattedMessage
              id="checkout"
              defaultMessage="Checkout"
              description="Checkout button text"
            />
          </ActionButton>
        </Box>
      </ButtonContainer>
    </CartHoverContainer>,
    document.body
  );
};

// TODO ICFE-424 - Clean-up CartPreview / remove unused props / optimize components / decompose it into modules / use custom hook for detecting clicks outside of a component

const WrappedCartPreview = () => {
  const {
    cartEntries,
    calculateCartTotal,
    checkoutPriceLimit,
    canUserCheckout,
    isCartTotalGreaterThanDiscountOfferValue,
    isCartEmpty,
    cartPreviewEntries,
    numCartPreviewEntries,
  } = useOrderContext();
  useLoyaltyRewardsFeedback(cartEntries);
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { selectedOffer: selectedCbaOffer, onCancelRedemption } = useOffersContext();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const cartPreviewID = useIdCounter('cartPreview');
  const { formatCurrencyForLocale } = useUIContext();
  const cartButton = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const hide = useCallback(() => setVisible(false), []);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const discountAppliedCmsOffer = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);
  const toggleVisible = useCallback(() => setVisible(state => !state), []);
  const prevDiscountAppliedOffers = usePrevious(discountAppliedCmsOffer);
  const isCbaDiscountOffer = selectedCbaOffer?.option?._type === MenuObjectTypes.OFFER_DISCOUNT;
  const { reversedUI } = useReversedUIContext();

  const discountOffers = useMemo(
    () =>
      isCbaDiscountOffer
        ? [selectedCbaOffer]
        : discountAppliedCmsOffer
        ? [discountAppliedCmsOffer]
        : [],
    [discountAppliedCmsOffer, isCbaDiscountOffer, selectedCbaOffer]
  );

  useEffectOnUpdates(() => {
    if (visible || isUndefined(prevDiscountAppliedOffers)) {
      return;
    }
    setVisible(true);

    // Needs setTimeout to work, might be due to conflict
    // w/reach-router's focus management since this is
    // typically triggered at the same time as a route change?
    window.setTimeout(() => {
      cartButton.current?.focus();
    }, 100);
  }, [cartEntries.length, discountOffers?.length]);

  useEffect(() => {
    if (!cartEntries.length) {
      LocalStorage.removeItem(StorageKeys.FREE_MODAL_ITEMS_SHOWED_IN_CART);
    }
  }, [cartEntries]);

  const onConfirmAlert = () => {
    onCancelRedemption();
  };

  const onCancelAlert = () => {
    navigate(routes.menu);
  };

  const [OfferCheckoutConfirmationModal, openOfferCheckoutConfirmationModal] = useDialogModal({
    showCancel: true,
    onConfirm: onConfirmAlert,
    onDismiss: onCancelAlert,
    onCancel: onCancelAlert,
    modalAppearanceEventMessage: 'Valid offers',
  });

  return (
    <>
      <ActionButtonStyled
        variant={defaultConfig.buttonVariant}
        size={ActionButtonSizes.SMALL}
        data-testid="cart-button-desktop"
        onClick={toggleVisible}
        className="ignore-react-onclickoutside"
        aria-expanded={visible}
        aria-controls={cartPreviewID}
        ref={cartButton}
        title="Shopping Cart"
        reversed={reversedUI}
        startIcon={<Icon icon="cart" color="icon-default" aria-hidden />}
        endIcon={false}
      >
        <VisuallyHidden>{formatMessage({ id: 'shoppingCartPreview' })}</VisuallyHidden>
        {defaultConfig.showNumItems
          ? numCartPreviewEntries
          : formatCurrencyForLocale(calculateCartTotal())}
        <VisuallyHidden>
          {defaultConfig.showNumItems
            ? formatMessage({ id: 'items' })
            : formatMessage({ id: 'cartTotal' })}
        </VisuallyHidden>
      </ActionButtonStyled>
      {visible && (
        <CartPreview
          id={cartPreviewID}
          calculateCartTotal={calculateCartTotal}
          checkoutPriceLimit={checkoutPriceLimit}
          discountOffers={discountOffers}
          isStoreOpenAndAvailable={isStoreOpenAndAvailable}
          openOfferCheckoutConfirmationModal={openOfferCheckoutConfirmationModal}
          canUserCheckout={canUserCheckout}
          isCartTotalGreaterThanDiscountOfferValue={isCartTotalGreaterThanDiscountOfferValue}
          isCartEmpty={isCartEmpty}
          cartPreviewEntries={cartPreviewEntries}
          cartEntries={cartEntries}
          navigate={navigate}
          loyaltyEnabled={loyaltyEnabled}
          appliedLoyaltyRewards={appliedLoyaltyRewards}
          hideCartPreview={hide}
          cartButton={cartButton}
        />
      )}
      <OfferCheckoutConfirmationModal
        heading={formatMessage({ id: 'headsUpModalTitle' })}
        body={formatMessage({ id: 'redeemOfferConfirmation' })}
        confirmLabel={formatMessage({ id: 'continueWithoutOffer' })}
        cancelLabel={formatMessage({ id: 'returnToMenu' })}
      />
    </>
  );
};

export default WrappedCartPreview;
